import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { PersonalRequisitionWithRelations } from '@api-swagger-client/index';
import {
    ApprovalChainItem,
    StatusTone,
    fullName,
    initials,
    statusIcon,
    statusTone,
} from '@app/@shared/approval-detail/approval-detail.utils';
import { LinkExpirationModule } from '@app/@shared/link-expiration/link-expiration.module';
import { TemporalLinkService } from '@app/@shared/temporal-link.service';
import { PersonalRequisitionService } from '@app/client/personal-requisition/personal-requisition.service';
import { UserService } from '@app/users/user.service';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { timer } from 'rxjs';

@Component({
    selector: 'app-personal-requisition-authorization',
    standalone: true,
    imports: [
        CommonModule,
        LinkExpirationModule,
        ButtonModule,
        DialogModule,
        FormsModule,
        ProgressSpinnerModule,
        InputTextareaModule,
        AvatarModule,
        ToastModule,
    ],
    providers: [MessageService],

    templateUrl: './personal-requisition-authorization.component.html',
    styleUrl: './personal-requisition-authorization.component.scss',
})
export class PersonalRequisitionAuthorizationComponent {
    isLoading = false;
    linkToken: string | null = null;
    referenceId: string | null = null;
    loading: boolean = true;
    error: string | null = null;
    success: boolean = false;
    verifying: boolean = false;
    linkDetails: any = null;
    userToken: string | null = null;

    // Link expiration properties
    expirationDate: Date | null = null;
    linkExpired: boolean = false;

    requisitionId: string | null = null;
    requisition: PersonalRequisitionWithRelations | null = null;
    processingAction = false;

    // Dialogs
    submitDialogVisible = false;
    approveDialogVisible = false;
    rejectDialogVisible = false;
    commentText = '';
    commentError = false;

    // Available actions for the current user
    availableActions: string[] = [];

    // Aprobadores; se arma al cargar para no recrear el arreglo en cada ciclo
    approvalChain: ApprovalChainItem[] = [];
    constructor(
        private store: Store,
        private router: Router,
        private route: ActivatedRoute,
        private messageService: MessageService,
        private temporalLinkService: TemporalLinkService,
        private userService: UserService,
        private personalRequisitionService: PersonalRequisitionService
    ) {}

    ngOnInit(): void {
        this.linkToken = this.route.snapshot.queryParams['token'] || null;
        this.userToken = this.route.snapshot.queryParams['userToken'] || null;
        this.referenceId =
            this.route.snapshot.queryParams['referenceId'] || null;
        this.requisitionId = this.referenceId;
        if (this.linkToken !== null) {
            this.validateLink();
        } else {
            this.loading = false;
        }
    }

    validateLink(): void {
        if (!this.linkToken) {
            this.error = 'Token no proporcionado';
            this.loading = false;
            return;
        }

        this.temporalLinkService
            .validateTemporalLink(this.linkToken)
            .subscribe({
                next: (response) => {
                    if (response && response.valid) {
                        this.success = true;
                        this.linkDetails = response.link;
                        this.getRequisitionDetail();
                        if (this.linkDetails.expirationDate) {
                            this.expirationDate = new Date(
                                this.linkDetails.expirationDate
                            );
                            const now = new Date();
                            if (this.expirationDate < now) {
                                this.linkExpired = true;
                                this.showToast(
                                    'warning',
                                    'Enlace expirado',
                                    'El enlace para ver la requisición ha expirado'
                                );
                            }
                        }

                        if (this.linkDetails.used) {
                            timer(1000).subscribe(() => {
                                this.messageService.add({
                                    severity: 'info',
                                    summary: 'Enlace ya utilizado',
                                    detail: 'Este enlace para ver la requisición ya ha sido utilizado anteriormente',
                                    life: 3000,
                                });
                            });
                        }
                    } else {
                        this.linkExpired = true;
                        this.error =
                            'El enlace para ver la requisición ha expirado. Por favor, solicite uno nuevo.';
                        this.showToast(
                            'error',
                            'Enlace expirado',
                            'El enlace para ver la requisición ha expirado. Por favor, solicite uno nuevo.'
                        );
                    }
                    this.loading = false;
                },
                error: (err) => {
                    this.error = err.message || 'Error al verificar el enlace';
                    this.loading = false;

                    console.error('Error al verificar el enlace:', err);

                    timer(1000).subscribe(() => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error de verificación',
                            detail:
                                err.error?.message ||
                                'Ha ocurrido un error al verificar el enlace',
                            life: 3000,
                        });
                    });
                },
            });
    }

    onExpirationStatusChanged(expired: boolean): void {
        this.linkExpired = expired;
        if (expired) {
            this.showToast(
                'warning',
                'Enlace expirado',
                'El enlace para autorizar la requisición ha expirado'
            );
        }
    }

    showToast(
        severity: string = 'info',
        summary: string = '',
        detail: string = ''
    ) {
        this.messageService.add({
            key: 'tst',
            severity: severity,
            summary: summary,
            detail: detail,
        });
    }

    getStatusTone(statusCode: string): StatusTone {
        return statusTone(statusCode);
    }

    getStatusIcon(statusCode: string): string {
        return statusIcon(statusCode);
    }

    fullName = fullName;
    initials = initials;

    // Actions handling
    hasAction(action: string): boolean {
        return this.availableActions.includes(action);
    }

    submitRequisition(): void {
        this.submitDialogVisible = true;
        this.commentText = '';
        this.commentError = false;
    }

    approveRequisition(): void {
        this.approveDialogVisible = true;
        this.commentText = '';
        this.commentError = false;
    }

    rejectRequisition(): void {
        this.rejectDialogVisible = true;
        this.commentText = '';
        this.commentError = false;
    }

    cancelDialog(): void {
        this.submitDialogVisible = false;
        this.approveDialogVisible = false;
        this.rejectDialogVisible = false;
        this.commentText = '';
        this.commentError = false;
    }

    confirmSubmit(): void {
        if (!this.commentText.trim()) {
            this.commentError = true;
            return;
        }

        this.processingAction = true;

        this.personalRequisitionService
            .submitForApproval(this.requisitionId!, {
                comments: this.commentText,
            })
            .subscribe({
                next: (response) => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Requisición enviada',
                        detail: 'La requisición ha sido enviada para aprobación',
                    });
                    this.submitDialogVisible = false;
                    this.processingAction = false;
                    this.getRequisitionDetail();
                },
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail:
                            error.error?.message ||
                            'Error al enviar la requisición',
                    });
                    this.processingAction = false;
                },
            });
    }

    confirmApprove(): void {
        if (!this.commentText.trim()) {
            this.commentError = true;
            return;
        }

        this.processingAction = true;

        this.personalRequisitionService
            .approveRequisitionPublic(this.requisitionId!, this.linkToken, {
                comments: this.commentText,
            })
            .subscribe({
                next: (response) => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Requisición aprobada',
                        detail: 'La requisición ha sido aprobada correctamente',
                    });
                    this.approveDialogVisible = false;
                    this.processingAction = false;
                    this.getRequisitionDetail();
                },
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail:
                            error.error?.message ||
                            'Error al aprobar la requisición',
                    });
                    this.processingAction = false;
                },
            });
    }

    confirmReject(): void {
        if (!this.commentText.trim()) {
            this.commentError = true;
            return;
        }

        this.processingAction = true;

        this.personalRequisitionService
            .rejectRequisitionPublic(this.requisitionId!, this.linkToken, {
                comments: this.commentText,
            })
            .subscribe({
                next: (response) => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Requisición rechazada',
                        detail: 'La requisición ha sido rechazada',
                    });
                    this.rejectDialogVisible = false;
                    this.processingAction = false;
                    this.getRequisitionDetail();
                },
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail:
                            error.error?.message ||
                            'Error al rechazar la requisición',
                    });
                    this.processingAction = false;
                },
            });
    }

    private buildApprovalChain(): ApprovalChainItem[] {
        if (!this.requisition) {
            return [];
        }
        const chain: ApprovalChainItem[] = [
            { role: 'Jefe inmediato', person: this.requisition.immediateSuperior },
            { role: 'Director de área', person: this.requisition.areaManager },
        ];
        if (this.requisition.financeDirector) {
            chain.push({ role: 'Director de finanzas', person: this.requisition.financeDirector });
        }
        chain.push({ role: 'Director general', person: this.requisition.generalManager });
        return chain;
    }

    private async getRequisitionDetail(): Promise<void> {
        this.loading = true;

        try {
            // Get requisition details
            this.personalRequisitionService
                .findByIdFromLink(this.requisitionId!, this.linkToken)
                .subscribe({
                    next: (response) => {
                        this.requisition = response.data;
                        this.approvalChain = this.buildApprovalChain();
                        this.availableActions =
                            response['data']['actions'] || [];
                        // Sort histories by date (newest first)
                        if (this.requisition?.requisitionStatusHistories) {
                            this.requisition.requisitionStatusHistories.sort(
                                (a, b) =>
                                    new Date(b.createdOn).getTime() -
                                    new Date(a.createdOn).getTime()
                            );
                        }

                        // Sort comments by date (newest first)
                        if (this.requisition?.requisitionComments) {
                            this.requisition.requisitionComments.sort(
                                (a, b) =>
                                    new Date(b.createdOn).getTime() -
                                    new Date(a.createdOn).getTime()
                            );
                        }

                        this.loading = false;
                    },
                    error: (error) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'No se pudo cargar la información de la requisición',
                        });
                        this.loading = false;
                    },
                });
        } catch (error) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al cargar la requisición',
            });
            this.loading = false;
        }
    }
}
